import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { Component, Inject, OnInit, ViewChild, Optional, Output, EventEmitter, Input, ElementRef, ChangeDetectorRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { ConfirmarAltaTicketDialogComponent } from '../confirmar-alta-ticket-dialog/confirmar-alta-ticket-dialog.component';
import { TicketService } from '../../services/ticket.service';
import { ITicket, ITicketPayload } from '../../models/ticket.model';
import { AppAlertService } from '@shared/service/app/alert.service';
import { Router } from '@angular/router';
import { NavigationService } from '@core/navigation/navigation.service';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { AdjuntosComponent } from '@shared/components/adjuntos/adjuntos.component';
import { Subject, takeUntil } from 'rxjs';
import { FuseAlertComponent } from '@fuse/components/alert';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { environment } from 'environments/environment';
import { HeaderTextService } from '@shared/components/header-text/header-text.service';

@Component({
    selector: 'app-alta-ticket',
    templateUrl: './alta-ticket.component.html',
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                width: 100%;
                height: 100%;
                min-height: 0;
                overflow: hidden;
            }
        `
    ],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatIconModule,
        MatTooltipModule,
        AdjuntosComponent,
        FuseAlertComponent
    ]
})
export class AltaTicketComponent implements OnInit {
    private readonly DRAFT_STORAGE_KEY = 'jedstion_ticket_draft';
    tieneBorradorGuardado: boolean = false;

    ticketForm: FormGroup;
    loading: boolean = false;
    color_primario: string;
    moduloActual: string = '';
    menuActual: string = '';
    
    isDetailMode: boolean = false;
    activeTicket: ITicket | null = null;
    default_color: string = 'bg-primary';
    adjuntoSeleccionadoPreview: { url: string; nombre: string; extension: string } | null = null;
    selectedPreview: string | null = null;
    
    @Input() isDrawer: boolean = false;
    @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;
    
    // Alertas manejadas globalmente por AppAlertService

    @Output() cerrado: EventEmitter<void> = new EventEmitter<void>();

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _currentUser: User | null = null;

    constructor(
        private _fb: FormBuilder,
        @Optional() private _dialogRef: MatDialogRef<AltaTicketComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
        private _ticketService: TicketService,
        private _alertService: AppAlertService,
        private _router: Router,
        private _navigationService: NavigationService,
        private _empresaService: EmpresaService,
        private _userService: UserService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _dialog: MatDialog,
        private _cdr: ChangeDetectorRef,
        private _headerTextService: HeaderTextService
    ) {
        this.ticketForm = this._fb.group({
            modulo: ['', [Validators.required]],
            menu: ['', [Validators.required]],
            descripcion: ['', [Validators.required, Validators.maxLength(255)]],
            detalle: ['', [Validators.required]],
            usuario: [{ value: '', disabled: true }]
        });
    }

    ngOnInit(): void {
        // Si no se abrió vía MatDialog, asumimos modo drawer / embebido para ocupar el 100% de la pantalla
        if (!this._dialogRef) {
            this.isDrawer = true;
        }

        // Obtener colores de la empresa para mantener la estética
        this._empresaService.empresa$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(empresa => {
                this.color_primario = empresa?.color_primario;
            });

        // Asegurar la carga de datos de la empresa
        this._empresaService.getEmpresa();

        // Suscribirse al usuario actual para cargar el "Usuario que reportó" (como fallback)
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this._currentUser = user;
                if (!this.isDetailMode) {
                    const extUser = this._userService.getExternalUser();
                    if (!extUser) {
                        this.ticketForm.patchValue({
                            usuario: user?.name || ''
                        });
                    }
                }
            });

        // Suscribirse reactivamente al usuario externo (con prioridad)
        this._userService.externalUser$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((extUser) => {
                if (!this.isDetailMode && extUser) {
                    this.ticketForm.patchValue({
                        usuario: extUser.name
                    });
                }
            });

        // Suscribirse al ticket activo para determinar si es modo creación o detalle
        this._ticketService.activeTicket$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(ticket => {
                this.activeTicket = ticket;
                if (ticket) {
                    this.isDetailMode = true;
                    this.ticketForm.patchValue({
                        modulo: ticket.modulo,
                        menu: ticket.menu,
                        descripcion: ticket.descripcion.replace('JED - ', ''), // Quitar prefijo si existe
                        detalle: ticket.detalle,
                        usuario: ticket.usuario || ''
                    });
                    this.ticketForm.disable(); // Modo lectura (deshabilita todo el formulario)
                } else {
                    this.isDetailMode = false;
                    
                    // Guardar el valor actual del usuario antes del reset
                    const currentUsuarioVal = this.ticketForm.get('usuario')?.value;

                    this.ticketForm.enable();
                    this.ticketForm.reset();
                    
                    // El campo usuario siempre debe permanecer deshabilitado (no modificable)
                    this.ticketForm.get('usuario')?.disable();
                    
                    // Reasignar el usuario logueado tras el reset
                    const extUser = this._userService.getExternalUser();
                    if (extUser) {
                        this.ticketForm.patchValue({
                            usuario: extUser.name
                        });
                    } else if (this._currentUser) {
                        this.ticketForm.patchValue({
                            usuario: this._currentUser.name || ''
                        });
                    } else if (currentUsuarioVal) {
                        this.ticketForm.patchValue({
                            usuario: currentUsuarioVal
                        });
                    } else {
                        // Respaldo directo en caso de carga inicial asíncrona contemplando si es embebido
                        const isEmbedded = window.self !== window.top;
                        const userRaw = isEmbedded ? sessionStorage.getItem('user') : localStorage.getItem('user');
                        if (userRaw) {
                            try {
                                const currentUser = JSON.parse(userRaw);
                                this.ticketForm.patchValue({
                                    usuario: currentUser?.name || ''
                                });
                            } catch (e) {
                                console.error('Error al parsear el usuario', e);
                            }
                        }
                    }
                    
                    this.inferirContexto();
                    this.cargarBorradorSiExiste();
                }
            });

        if (typeof window !== 'undefined') {
            window.addEventListener('message', this.handleWindowMessage);
        }
    }

    /**
     * Intenta determinar el módulo y menú actual basándose en la URL,
     * jerarquía de rutas del NavigationService y el HeaderTextService.
     */
    inferirContexto(): void {
        const url = this._router.url;
        // Quitar parámetros de consulta si existen
        const path = url.split('?')[0];

        let menuItem: any = null;
        let matchedPath = path;

        // 1. Probar ruta exacta
        menuItem = this._navigationService.getItemByRoute(path);

        // 2. Si no coincide (pantalla de alta/edición como /fabricados/nuevo-producto o sub-rutas),
        // ir retrocediendo segmentos para encontrar el menú padre
        if (!menuItem) {
            const segments = path.split('/').filter(s => s !== '');
            while (segments.length > 0 && !menuItem) {
                segments.pop();
                const parentPath = '/' + segments.join('/');
                if (parentPath && parentPath !== '/') {
                    menuItem = this._navigationService.getItemByRoute(parentPath);
                    if (menuItem) {
                        matchedPath = parentPath;
                        break;
                    }
                }
            }
        }

        // 3. Determinar el Menú:
        // Intentar obtener el título personalizado de la pantalla si existe (ej: "Nuevo Producto", "Editar Venta")
        const headerInfo = this._headerTextService ? this._headerTextService.getTitulo() : null;
        let customTitle = '';
        if (typeof headerInfo === 'string') {
            customTitle = headerInfo;
        } else if (headerInfo && typeof headerInfo === 'object' && (headerInfo as any).titulo) {
            customTitle = (headerInfo as any).titulo;
        }

        if (menuItem) {
            // Si hay un título específico de la vista de alta/edición/drawer, usarlo o combinarlo
            if (customTitle && customTitle !== menuItem.title && !customTitle.startsWith('/')) {
                this.menuActual = `${menuItem.title} - ${customTitle}`;
            } else {
                this.menuActual = menuItem.title;
            }
            this.moduloActual = this.deducirModulo(matchedPath);
        } else {
            // Fallback si no está en el menú de navegación
            this.menuActual = (customTitle && !customTitle.startsWith('/')) ? customTitle : '';
            this.moduloActual = this.deducirModulo(path);
        }

        this.ticketForm.patchValue({
            modulo: this.moduloActual,
            menu: this.menuActual
        });
    }

    deducirModulo(path: string): string {
        const segments = path.split('/').filter(s => s !== '');
        if (segments.length > 0) {
            // El primer segmento suele ser el módulo (ej: administracion, stock, ventas)
            const moduloKey = segments[0].toLowerCase();
            const modulosConocidos: { [key: string]: string } = {
                'stock': 'Stock',
                'ventas': 'Ventas',
                'administracion': 'Administración',
                'compras': 'Compras',
                'soporte': 'Soporte',
                'configuracion': 'Configuración',
                'indicadores': 'Indicadores',
                'contabilidad': 'Contabilidad',
                'recursos-humanos': 'Recursos Humanos',
                'rrhh': 'Recursos Humanos'
            };

            if (modulosConocidos[moduloKey]) {
                return modulosConocidos[moduloKey];
            }

            return segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
        }
        return '';
    }

    private _adjuntosSeleccionados: { nombre: string; extension: string; base64: string }[] = [];

    /**
     * Maneja el cambio de archivos desde el componente app-adjuntos
     */
    onArchivosChange(event: any[]): void {
        if (event && event.length > 0) {
            const timestamp = Date.now();
            this._adjuntosSeleccionados = event.map((file, index) => {
                const extension = (file.extension || '').replace('.', '');
                return {
                    nombre: `jedstion_adjunto_ticket_${timestamp}_${index + 1}.${extension}`,
                    extension: extension,
                    base64: file.base64
                };
            });

            // Auto-scroll suave hacia abajo para mostrar inmediatamente que el adjunto fue cargado
            setTimeout(() => {
                if (this.scrollContainer && this.scrollContainer.nativeElement) {
                    const el = this.scrollContainer.nativeElement;
                    el.scrollTo({
                        top: el.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        } else {
            this._adjuntosSeleccionados = [];
        }
    }

    enviar(): void {
        if (this.ticketForm.invalid) {
            this.ticketForm.markAllAsTouched();
            return;
        }

        const formValues = this.ticketForm.getRawValue();
        const { usuario, ...restValues } = formValues;

        const dialogRef = this._dialog.open(ConfirmarAltaTicketDialogComponent, {
            data: {
                descripcion: formValues.descripcion,
                detalle: formValues.detalle,
                modulo: formValues.modulo,
                menu: formValues.menu,
                usuario: usuario,
                adjuntos: this._adjuntosSeleccionados,
                color_primario: this.color_primario
            },
            panelClass: 'custom-dialog-panel',
            autoFocus: false,
            disableClose: false
        });

        dialogRef.afterClosed().subscribe((confirmado: boolean) => {
            if (confirmado) {
                this._ejecutarCreacionTicket(restValues, usuario, formValues.descripcion);
            }
        });
    }

    private _ejecutarCreacionTicket(restValues: any, usuario: string, descripcion: string): void {
        this.loading = true;

        const payload: ITicketPayload = {
            ...restValues,
            usuario_externo_nombre: usuario,
            descripcion: 'JED - ' + descripcion,
            prioridad: 0,
            adjuntos: this._adjuntosSeleccionados
        };

        this._ticketService.crearTicket(payload).subscribe({
            next: (res) => {
                this.loading = false;
                if (res.success) {
                    this.limpiarBorrador();
                    this._alertService.success(res.mensaje || 'Ticket creado exitosamente.');
                    this._ticketService.notificarTicketCreado();
                    
                    // Esperar un momento para que el usuario vea el mensaje de éxito antes de cerrar
                    setTimeout(() => {
                        this.loading = false;
                        this.cerrar();
                    }, 1500);
                } else {
                    this.loading = false;
                    this._alertService.error(res.mensaje || 'Error al crear el ticket.');
                }
            },
            error: (err) => {
                this.loading = false;
                console.error('Error creando ticket:', err);
                this._alertService.error('Error interno al procesar la solicitud.');
            }
        });
    }

    /**
     * Verifica si hay cambios en el formulario o adjuntos cargados
     */
    tieneCambios(): boolean {
        if (this.isDetailMode) return false;
        const desc = this.ticketForm.get('descripcion')?.value || '';
        const detalle = this.ticketForm.get('detalle')?.value || '';
        const tieneAdjuntos = this._adjuntosSeleccionados && this._adjuntosSeleccionados.length > 0;
        return desc.trim().length > 0 || detalle.trim().length > 0 || tieneAdjuntos;
    }

    /**
     * Unifica y normaliza los adjuntos del ticket activo soportando tanto
     * el nuevo array 'adjuntos' como el string 'adjunto' de tickets anteriores.
     */
    get adjuntosDetalle(): { url: string; nombre: string; extension: string }[] {
        if (!this.activeTicket) return [];

        const lista: { url: string; nombre: string; extension: string }[] = [];

        // 1. Caso nuevos: Array 'adjuntos'
        if (Array.isArray(this.activeTicket.adjuntos) && this.activeTicket.adjuntos.length > 0) {
            this.activeTicket.adjuntos.forEach((adj: any) => {
                if (!adj) return;
                const rawUrl = typeof adj === 'string' ? adj : (adj.url || adj.path || adj.nombre || '');
                const extension = (adj.extension || rawUrl.split('.').pop() || '').toLowerCase().replace('.', '');
                const nombre = adj.nombre || rawUrl.split('/').pop() || 'Adjunto';
                lista.push({ url: rawUrl, nombre, extension });
            });
        }
        // 2. Caso anteriores: String 'adjunto' individual
        else if (this.activeTicket.adjunto && typeof this.activeTicket.adjunto === 'string' && this.activeTicket.adjunto.trim() !== '') {
            const rawUrl = this.activeTicket.adjunto.trim();
            const extension = (rawUrl.split('.').pop() || '').toLowerCase().replace('.', '');
            const nombre = rawUrl.split('/').pop() || 'Adjunto';
            lista.push({ url: rawUrl, nombre, extension });
        }

        return lista;
    }

    esImagenAdjunto(extension: string): boolean {
        const ext = (extension || '').toLowerCase();
        return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'].includes(ext);
    }

    obtenerIconoAdjunto(extension: string): string {
        const ext = (extension || '').toLowerCase();
        if (['pdf'].includes(ext)) return 'heroicons_outline:document-text';
        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'heroicons_outline:archive-box';
        if (['xls', 'xlsx', 'csv'].includes(ext)) return 'heroicons_outline:table-cells';
        return 'heroicons_outline:paper-clip';
    }

    /**
     * Construye y sanitiza de manera segura la URL del archivo adjunto
     * evitando ataques de traversal path o esquemas no seguros.
     */
    obtenerUrlAdjuntoSegura(rawUrl: string): string {
        if (!rawUrl || typeof rawUrl !== 'string') return '';

        const trimmed = rawUrl.trim();

        // Si ya es una URL absoluta HTTP/HTTPS
        if (/^https?:\/\//i.test(trimmed)) {
            try {
                const parsedUrl = new URL(trimmed);
                if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
                    return parsedUrl.toString();
                }
            } catch (e) {
                return '';
            }
        }

        // Sanitizar contra path traversal y prefijos conflictivos
        let cleanPath = trimmed.replace(/(\.\.[\/\\])+/g, ''); // Remueve secuencias ../
        cleanPath = cleanPath.replace(/^[\\\/]+/, ''); // Remueve barras iniciales

        const baseAdjuntos = (environment.soporte?.base_adjuntos || 'https://apiregistro.projeds.com').replace(/\/+$/, '');
        return `${baseAdjuntos}/${cleanPath}`;
    }

    abrirAdjuntoDetalle(adj: { url: string; nombre: string; extension: string }, event?: MouseEvent): void {
        if (!adj || !adj.url) return;

        const finalUrl = this.obtenerUrlAdjuntoSegura(adj.url);
        if (!finalUrl) return;

        if (this.esImagenAdjunto(adj.extension)) {
            this.selectedPreview = finalUrl;
            this._cdr.detectChanges();
        }
        window.open(finalUrl, '_blank', 'noopener');
    }

    /**
     * Guarda el contenido actual del ticket en localStorage como borrador.
     */
    guardarBorradorLocal(): void {
        try {
            const draft = {
                modulo: this.ticketForm.get('modulo')?.value || '',
                menu: this.ticketForm.get('menu')?.value || '',
                descripcion: this.ticketForm.get('descripcion')?.value || '',
                detalle: this.ticketForm.get('detalle')?.value || '',
                timestamp: Date.now()
            };
            localStorage.setItem(this.DRAFT_STORAGE_KEY, JSON.stringify(draft));
            this.tieneBorradorGuardado = true;
        } catch (e) {
            console.error('Error guardando borrador del ticket:', e);
        }
    }

    /**
     * Intenta recuperar y restaurar un borrador previamente guardado.
     */
    cargarBorradorSiExiste(): boolean {
        try {
            const raw = localStorage.getItem(this.DRAFT_STORAGE_KEY);
            if (!raw) {
                this.tieneBorradorGuardado = false;
                return false;
            }
            const draft = JSON.parse(raw);
            if (draft && (draft.descripcion || draft.detalle)) {
                this.ticketForm.patchValue({
                    modulo: draft.modulo || this.moduloActual,
                    menu: draft.menu || this.menuActual,
                    descripcion: draft.descripcion || '',
                    detalle: draft.detalle || ''
                });
                this.tieneBorradorGuardado = true;
                this._cdr.markForCheck();
                return true;
            }
        } catch (e) {
            console.error('Error cargando borrador del ticket:', e);
        }
        this.tieneBorradorGuardado = false;
        return false;
    }

    /**
     * Elimina el borrador guardado y reinicia el formulario
     */
    descartarBorrador(): void {
        this.limpiarBorrador();
        this.ticketForm.patchValue({
            descripcion: '',
            detalle: ''
        });
        this.inferirContexto();
        this._alertService.info('Borrador descartado.');
        this._cdr.markForCheck();
    }

    /**
     * Elimina el borrador de localStorage
     */
    limpiarBorrador(): void {
        try {
            localStorage.removeItem(this.DRAFT_STORAGE_KEY);
            this.tieneBorradorGuardado = false;
        } catch (e) {
            console.error('Error eliminando borrador:', e);
        }
    }

    cancelar(): void {
        this.solicitarCierre();
    }

    /**
     * Pide confirmación al usuario si hay datos cargados antes de cerrar,
     * ofreciendo la opción principal y recomendada de guardar como borrador.
     */
    solicitarCierre(): void {
        if (this.tieneCambios()) {
            const dialogRef = this._fuseConfirmationService.open({
                title: '¿Guardar ticket como borrador?',
                message: 'Tienes información cargada. Puedes guardarla como borrador para continuar editándola más tarde (por ejemplo, para adjuntar capturas).',
                icon: {
                    show: true,
                    name: 'heroicons_outline:document-duplicate',
                    color: 'info'
                },
                actions: {
                    confirm: {
                        show: true,
                        label: 'Guardar como borrador',
                        color: 'primary'
                    },
                    cancel: {
                        show: true,
                        label: 'Descartar cambios'
                    }
                },
                dismissible: true
            });

            dialogRef.afterClosed().subscribe((res) => {
                if (res === 'confirmed') {
                    this.guardarBorradorLocal();
                    this._alertService.info('Borrador guardado. Podrás continuar editándolo cuando vuelvas.');
                    this.cerrar();
                } else if (res === 'cancelled') {
                    // El usuario eligió descartar cambios
                    this.limpiarBorrador();
                    this.cerrar();
                }
                // Si cerró haciendo click afuera o escape, no cerramos para que pueda continuar editando
            });
        } else {
            this.cerrar();
        }
    }

    cerrar(): void {
        if (this._dialogRef) {
            this._dialogRef.close();
        }
        this.cerrado.emit();
        if (typeof window !== 'undefined' && window.self !== window.top) {
            window.parent.postMessage({ type: 'CLOSE_TICKET_DRAWER' }, '*');
        }
    }

    /**
     * Obtiene el color correspondiente al estado del ticket siguiendo la paleta de la grilla
     */
    getEstadoColor(estado: string | undefined): string {
        if (!estado) return '#374151';
        
        const e = estado.toLowerCase();
        if (e.includes('pendiente')) return '#E11D48';  // Rojo/Rosa intenso
        if (e.includes('en curso')) return '#2563EB';   // Azul intenso
        if (e.includes('finalizado')) return '#16A34A'; // Verde intenso
        if (e.includes('publicado') || e.includes('deploy')) return '#8B5CF6'; // Morado para Publicado (Para Verificar)
        
        return '#374151'; // Gris por defecto
    }

    /**
     * Retorna el color de contraste (blanco o negro) basado en el color primario
     */
    getContrastColor(hexColor: string): string {
        if (!hexColor) return '#ffffff';
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return (yiq >= 128) ? '#0f172a' : '#ffffff';
    }

    private handleWindowMessage = (event: MessageEvent): void => {
        if (event.data && event.data.type === 'REQUEST_CLOSE_TICKET') {
            this.solicitarCierre();
        }
    };

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        if (typeof window !== 'undefined') {
            window.removeEventListener('message', this.handleWindowMessage);
        }
    }
}
